import { Box, IconButton, Menu, MenuButton, MenuDivider, MenuList, Text } from '@chakra-ui/react'
import {
  faCog,
  faCopy,
  faEllipsisH,
  faFlag,
  faHandPaper,
  faTimes,
  faUserMinus,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import SeriesHostQ from '@/queries/SeriesHostQ'
import SeriesQ from '@/queries/SeriesQ'
import SessionHostQ from '@/queries/SessionHostQ'
import SessionQ from '@/queries/SessionQ'
import UserQ from '@/queries/UserQ'
import WorldMemberQ from '@/queries/WorldMemberQ'
import WorldQ from '@/queries/WorldQ'
import { useUser } from '@/client/context/user'
import { useServerNow } from '@/client/hooks'
import type { Query } from '@/client/query'
import { RPCRefresh } from '@/client/rpc'
import { getAdminUrl } from '@/client/services/url'
import { hasUserRole } from '@/client/services/user'

import type { HostToastInfo } from '@/components/Host/CohostReviewModal/types'
import HostTable from '@/components/Host/HostTable'
import { getSeriesHostAvatarDetails, getSessionHostAvatarDetails } from '@/components/Host/helpers'
import useCohostCancelRequestModal from '@/components/Host/useCohostCancelRequestModal'
import useCohostRemoveSelfModal from '@/components/Host/useCohostRemoveSelfModal'
import useCohostRequestModal from '@/components/Host/useCohostRequestModal'
import ReportModalV2 from '@/components/Report/ReportModalV2'
import ShareMenuGroup from '@/components/Share/ShareMenuGroup'
import ShwLink from '@/components/ShwLink'
import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import MenuItem from '@/components/UI/MenuItem'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import Tippy from '@/components/UI/Tippy'

type MoreOptionsButtonParams = {
  type: 'session' | 'series'
  tutorId: number
  typeId: number
  shareOptions: {
    url: string
    description: string
    shortDescription: string
  }
  duplicateOptions?: {
    url: string
  }
  sessionOrSeries: Query
  iconColor?: string
}

export default function MoreOptionsButton({
  type,
  tutorId,
  typeId,
  shareOptions,
  duplicateOptions,
  sessionOrSeries,
  iconColor,
}: MoreOptionsButtonParams): React.ReactElement | null {
  const { url, description, shortDescription } = shareOptions
  const currentUser = useUser()
  const toast = useToaster()

  const [showReportModal, setShowReportModal] = useState(false)
  const [CohostRequestModal, showCohostRequestModal] = useCohostRequestModal()
  const [CohostCancelRequestModal, showCohostCancelRequestModal] = useCohostCancelRequestModal()
  const [CohostRemoveSelfModal, showCohostRemoveSelfModal] = useCohostRemoveSelfModal()
  const [removeCohostInfo, setRemoveCohostInfo] = useState<HostToastInfo | null>(null)
  const [showTableModal, setShowTableModal] = useState(false)

  const isSession = type === 'session'

  const sessionStartDate = isSession ? `${sessionOrSeries(SessionQ.start_date)}` : ''
  const sessionStartDateTime = DateTime.fromISO(sessionStartDate)
  const now = useServerNow()
  const sessionHasStarted = sessionStartDateTime.isValid && sessionStartDateTime < now
  const allowsCohosting =
    (isSession
      ? sessionOrSeries(SessionQ.allow_cohosts)
      : sessionOrSeries(SeriesQ.allow_cohosts)) ?? false
  const isSeriesSession = isSession ? sessionOrSeries(SessionQ.series_id) != null : false
  const hasSeriesEnded = isSession ? false : sessionOrSeries(SeriesQ.isClosed()) === true

  const isUserMainTutor = tutorId === currentUser.id

  const isHost =
    (isSession
      ? sessionOrSeries(SessionQ.isHost({ userId: currentUser.id }))
      : sessionOrSeries(SeriesQ.isHost({ userId: currentUser.id }))) ?? false

  const world = isSession
    ? sessionOrSeries.one(SessionQ.getWorld())
    : sessionOrSeries.one(SeriesQ.getWorld())
  const worldId = world(WorldQ.id)
  const worldTitle = world(WorldQ.title) ?? ''

  const isTutorInSubworld = isSession
    ? (sessionOrSeries<boolean>`EXISTS(SELECT 1 ${WorldMemberQ.where(
        {
          worldId: SessionQ.world_id,
          userId: currentUser.id,
        },
        WorldMemberQ.isAtLeastLevel('tutor')
      )})` ?? false)
    : (sessionOrSeries<boolean>`EXISTS(SELECT 1 ${WorldMemberQ.where(
        {
          worldId: SeriesQ.world_id,
          userId: currentUser.id,
        },
        WorldMemberQ.isAtLeastLevel('tutor')
      )})` ?? false)
  const needsToBeTutorInSubworld = worldId != null && !isTutorInSubworld
  const requestedHost =
    (isSession
      ? sessionOrSeries(SessionQ.isHost({ userId: currentUser.id, approved: false }))
      : sessionOrSeries(SeriesQ.isHost({ userId: currentUser.id, approved: false }))) ?? false
  const viewingHost = isSession
    ? sessionOrSeries.one(SessionHostQ.where({ sessionId: SessionQ.id, userId: currentUser.id }))
    : sessionOrSeries.one(SeriesHostQ.where({ seriesId: SeriesQ.id, userId: currentUser.id }))
  const hostId = isSession ? viewingHost(SessionHostQ.id) : (viewingHost(SeriesHostQ.id) ?? 0)

  const mainHost = isSession
    ? sessionOrSeries.one(
        SessionHostQ.where({
          sessionId: SessionQ.id,
          role: 'main',
          include: [
            {
              association: 'User',
              required: true,
            },
          ],
        })
      )
    : sessionOrSeries.one(
        SeriesHostQ.where({
          seriesId: SeriesQ.id,
          role: 'main',
          include: [
            {
              association: 'User',
              required: true,
            },
          ],
        })
      )
  const mainHostId = isSession ? mainHost(SessionHostQ.id) : (mainHost(SeriesHostQ.id) ?? 0)
  const mainHostName = mainHost(UserQ.display_name) ?? ''

  const hosts = isSession
    ? getSessionHostAvatarDetails(sessionOrSeries)
    : getSeriesHostAvatarDetails(sessionOrSeries)

  const canViewCohostingOption =
    hasUserRole(currentUser, 'user:tutor') &&
    !isUserMainTutor &&
    allowsCohosting &&
    !sessionHasStarted &&
    !isSeriesSession &&
    !hasSeriesEnded

  const canRemoveCohosts =
    (isUserMainTutor || hasUserRole(currentUser, 'admin:super')) &&
    hosts.length > 1 &&
    allowsCohosting &&
    !sessionHasStarted &&
    !isSeriesSession &&
    !hasSeriesEnded

  const removeFormInputs: FormInputType[] = useMemo(
    () => [
      {
        label: 'Write a message (optional)',
        type: 'textarea',
        name: 'reason',
        placeholder: `Let ${removeCohostInfo?.name} know why you are removing them`,
        required: false,
        size: 6,
      },
    ],
    [removeCohostInfo]
  )

  const handleRemove = useCallback(
    async (values: FormValues) => {
      if (removeCohostInfo == null) {
        return
      }
      if (isSession) {
        await RPCRefresh.removeSessionHost({
          sessionHostId: removeCohostInfo.hostId,
          deletedMessage: `${values.reason}`,
        })
      } else {
        await RPCRefresh.removeSeriesHost({
          seriesHostId: removeCohostInfo.hostId,
          deletedMessage: `${values.reason}`,
        })
      }
      toast({
        title: `You removed ${removeCohostInfo.name} as a co-host.`,
        status: 'success',
      })
      setRemoveCohostInfo(null)
    },
    [toast, removeCohostInfo, isSession]
  )

  return (
    <>
      <Menu>
        <MenuButton
          as={IconButton}
          variant="icon-outline"
          aria-label="See options"
          icon={
            <FontAwesomeIcon
              icon={faEllipsisH}
              color={iconColor != null ? chakraTokenToVar(iconColor) : undefined}
            />
          }
        />
        <MenuList border={`2px solid ${chakraTokenToVar('border.emphasized')}`}>
          {isHost && duplicateOptions != null ? (
            <Box>
              <ShwLink href={duplicateOptions.url} width="100%">
                <MenuItem value="duplicate" icon={faCopy} label="Duplicate Session" />
              </ShwLink>
            </Box>
          ) : null}

          {canRemoveCohosts ? (
            <MenuItem
              value="removeCohosts"
              icon={faUserMinus}
              label="Remove Co-hosts"
              onClick={() => setShowTableModal(true)}
            />
          ) : null}

          {canViewCohostingOption ? (
            requestedHost ? (
              <MenuItem
                value="cohostRequestCancel"
                icon={faTimes}
                label="Cancel Request to Co-host"
                onClick={showCohostCancelRequestModal}
              />
            ) : isHost ? (
              <MenuItem
                value="removeSelfCohost"
                icon={faUserMinus}
                color="fg.error"
                label="Remove Myself As Co-host"
                onClick={showCohostRemoveSelfModal}
              />
            ) : needsToBeTutorInSubworld ? (
              <Tippy content={`You are not a tutor in the ${worldTitle} subworld.`}>
                <Box>
                  <MenuItem
                    value="cohostRequestDisabled"
                    icon={faHandPaper}
                    label="Request to Co-Host"
                    disabled
                  />
                </Box>
              </Tippy>
            ) : (
              <MenuItem
                value="cohostRequest"
                icon={faHandPaper}
                label="Request to Co-host"
                onClick={showCohostRequestModal}
              />
            )
          ) : null}

          <MenuItem
            value="report"
            icon={faFlag}
            label="Report Tutor"
            onClick={() => setShowReportModal(true)}
          />

          <MenuDivider />

          <ShareMenuGroup url={url} description={description} shortDescription={shortDescription} />

          {hasUserRole(currentUser, 'admin:basic') ||
          hasUserRole(currentUser, 'admin:teach_for_america') ? (
            <>
              <MenuDivider />
              <Box>
                <ShwLink href={getAdminUrl(`user/${tutorId}`)} textDecor="none" width="100%">
                  <MenuItem value="userAdminPage" icon={faCog} label="User Page (Admin Only)" />
                </ShwLink>
              </Box>
              <Box>
                {type === 'series' ? (
                  <ShwLink href={getAdminUrl(`series/${typeId}`)} textDecor="none" width="100%">
                    <MenuItem
                      value="adminSeriesPage"
                      icon={faCog}
                      label="Series Page (Admin Only)"
                    />
                  </ShwLink>
                ) : (
                  <ShwLink href={getAdminUrl(`session/${typeId}`)} textDecor="none" width="100%">
                    <MenuItem
                      value="adminSessionPage"
                      icon={faCog}
                      label="Session Page (Admin Only)"
                    />
                  </ShwLink>
                )}
              </Box>
            </>
          ) : null}
        </MenuList>
      </Menu>
      {/* TODO: allow reporting of co-hosts */}
      <ReportModalV2
        entity={{ type: `${type}-host`, id: mainHostId ?? 0 }}
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        reportedUserName={mainHostName}
      />
      <CohostRequestModal
        subjectUserId={currentUser.id}
        sessionOrSeriesId={typeId}
        isSession={isSession}
      />
      <CohostCancelRequestModal hostId={hostId} isSession={isSession} />
      <CohostRemoveSelfModal hostId={hostId} mainHostId={tutorId} isSession={isSession} />

      <ModalV3
        open={showTableModal}
        onClose={() => {
          setShowTableModal(false)
        }}
        title="Hosts"
        size="xl"
        body={() => (
          <HostTable
            hosts={hosts}
            canRemoveHosts={canRemoveCohosts}
            setRemoveCohostInfo={setRemoveCohostInfo}
            setShowTableModal={setShowTableModal}
          />
        )}
      />
      <ModalV3
        title="Remove Co-host"
        size="xl"
        open={removeCohostInfo != null}
        onClose={() => {
          setRemoveCohostInfo(null)
          setShowTableModal(true)
        }}
        body={() => (
          <>
            <Text>
              Are you sure you want to remove {removeCohostInfo?.name} as a co-host? They will need
              to request again in order to become a co-host again.
            </Text>
            <Form name="CohostRemove" blocks={removeFormInputs} onSubmit={handleRemove} />
          </>
        )}
      />
    </>
  )
}
